import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';
import { getPageSEO } from '@/config/seoConfig';
import { useTranslation } from '@/hooks/useTranslation';

const Home = () => {
  const seoData = getPageSEO('home');
  const t = useTranslation();
  
  return (
    <>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        url={seoData.url}
        image={seoData.image}
        type="website"
      />
      <div className="min-h-screen">
      {/* Hero Section - Split Layout */}
      <section className="relative h-screen flex items-center overflow-hidden pt-24">
        {/* Left Side - João's Portrait */}
        <motion.div 
          initial={{ opacity: 0, x: -100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="w-[60%] sm:w-[55%] lg:w-2/5 h-full relative z-10"
        >
          <div className="relative h-full">
            <img
              src="/images/João_Xavier_HomePage.webP"
              alt="João Xavier"
              className="w-full h-full object-cover object-[center_20%] sm:object-[center_30%] md:object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/20"></div>
          </div>
        </motion.div>

        {/* Right Side - Content & Piano Background */}
        <div className="w-[40%] sm:w-[45%] lg:w-3/5 h-full relative flex items-center justify-center">
          {/* Piano Background for Right Side */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1520523839897-bd0b52f945a0?w=1920&q=80"
              alt="Piano keys background"
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-black/70 via-black/60 to-black/80"></div>
          </div>

          {/* Hero Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4 }}
            className="relative z-10 text-white px-2 sm:px-4 lg:px-16 text-center max-w-2xl"
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="font-playfair text-2xl sm:text-3xl md:text-7xl lg:text-8xl mb-2 sm:mb-3 lg:mb-6 tracking-tight font-semibold leading-tight"
            >
              {t.hero.title}
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="font-lora text-xs sm:text-sm md:text-2xl lg:text-3xl mb-4 sm:mb-6 lg:mb-12 text-gray-200 tracking-wide font-light"
            >
              {t.hero.subtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="flex flex-col gap-2 lg:gap-4 items-center justify-center sm:flex-row"
            >
              <Link to="/about">
                <Button
                  size="lg"
                  className="bg-white text-gray-900 hover:bg-gray-100 px-3 py-2 sm:px-4 sm:py-3 lg:px-8 lg:py-6 text-xs sm:text-xs lg:text-base font-medium transition-all duration-300"
                >
                  {t.hero.discoverMore}
                  <ArrowRight className="ml-1 lg:ml-2 w-3 h-3 lg:w-5 lg:h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  size="lg"
                  variant="ghost"
                  className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-3 py-2 sm:px-4 sm:py-3 lg:px-8 lg:py-6 text-xs sm:text-xs lg:text-base font-medium transition-all duration-300 bg-transparent"
                >
                  {t.hero.getInTouch}
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="absolute bottom-8 right-8 z-10 hidden lg:block"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2"
          >
            <motion.div className="w-1 h-2 bg-white/70 rounded-full" />
          </motion.div>
        </motion.div>
      </section>
    </div>
    </>
  );
};

export default Home;